<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar />

      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">

            <!-- Page Title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Lab Report</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('hospital-dashboard')">Hospital</a></li>
                      <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('lab-dashboard')">Laboratory</a></li>
                      <li class="breadcrumb-item active">Report</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary mb-3"></div>
              <p class="text-muted mb-0">Loading report...</p>
            </div>

            <!-- Report Card -->
            <div v-else class="row justify-content-center">
              <div class="col-lg-10">
                <!-- Action Buttons -->
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <button class="btn btn-light" @click="goTo('lab-dashboard')">
                    <i class="mdi mdi-arrow-left me-1"></i> Back to Dashboard
                  </button>
                  <div class="d-flex gap-2">
                    <button v-if="canVerify" class="btn btn-success" @click="verifyResult" :disabled="verifying">
                      <span v-if="verifying"><i class="mdi mdi-loading mdi-spin me-1"></i></span>
                      <span v-else><i class="mdi mdi-check-decagram me-1"></i></span>
                      Verify Result
                    </button>
                    <button class="btn btn-primary" @click="printReport">
                      <i class="mdi mdi-printer me-1"></i> Print Report
                    </button>
                  </div>
                </div>

                <!-- Report Content -->
                <div class="card" id="labReport">
                  <div class="card-body p-5">
                    <!-- Header -->
                    <div class="report-header text-center mb-4 pb-4 border-bottom">
                      <h3 class="mb-1">{{ facilityName }}</h3>
                      <p class="text-muted mb-2">Laboratory Services</p>
                      <h4 class="text-primary">LABORATORY TEST REPORT</h4>
                    </div>

                    <!-- Patient & Request Info -->
                    <div class="row mb-4">
                      <div class="col-md-6">
                        <h6 class="text-muted mb-3">PATIENT INFORMATION</h6>
                        <table class="table table-sm table-borderless mb-0">
                          <tr>
                            <td class="text-muted" style="width: 120px;">Name:</td>
                            <td class="fw-medium">{{ request.patientName }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted">Patient ID:</td>
                            <td>{{ request.patientID }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted">Gender / Age:</td>
                            <td>{{ request.gender }} / {{ request.age }} years</td>
                          </tr>
                          <tr>
                            <td class="text-muted">Blood Group:</td>
                            <td>{{ request.bloodGroup || '-' }}</td>
                          </tr>
                        </table>
                      </div>
                      <div class="col-md-6">
                        <h6 class="text-muted mb-3">REQUEST INFORMATION</h6>
                        <table class="table table-sm table-borderless mb-0">
                          <tr>
                            <td class="text-muted" style="width: 120px;">Request ID:</td>
                            <td class="fw-medium">{{ request.requestID }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted">Sample ID:</td>
                            <td>{{ request.sampleID }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted">Request Date:</td>
                            <td>{{ formatDate(request.requestDate || request.dateCreated) }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted">Ordered By:</td>
                            <td>{{ request.orderingDoctorName }}</td>
                          </tr>
                        </table>
                      </div>
                    </div>

                    <!-- Clinical Information -->
                    <div v-if="request.clinicalInfo" class="mb-4 p-3 bg-light rounded">
                      <h6 class="text-muted mb-2">CLINICAL INFORMATION</h6>
                      <p class="mb-0">{{ request.clinicalInfo }}</p>
                    </div>

                    <!-- Test Results Table -->
                    <div class="mb-4">
                      <h6 class="text-muted mb-3">TEST RESULTS</h6>
                      <table class="table table-bordered">
                        <thead class="table-light">
                          <tr>
                            <th>Test Name</th>
                            <th>Result</th>
                            <th>Unit</th>
                            <th>Reference Range</th>
                            <th>Flag</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-if="results.length === 0">
                            <td colspan="5" class="text-center text-muted py-3">No results available</td>
                          </tr>
                          <tr v-for="result in results" :key="result.resultID">
                            <td class="fw-medium">{{ result.testName || request.testName }}</td>
                            <td class="fw-bold" :class="getFlagClass(result.flag)">{{ result.result }}</td>
                            <td>{{ result.unit }}</td>
                            <td>{{ result.normalRange }}</td>
                            <td>
                              <span v-if="result.flag" class="badge" :class="getFlagBadge(result.flag)">{{ result.flag }}</span>
                              <span v-else class="text-success"><i class="mdi mdi-check-circle"></i> Normal</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Interpretation -->
                    <div v-if="hasInterpretation" class="mb-4 p-3 border rounded">
                      <h6 class="text-muted mb-2">INTERPRETATION</h6>
                      <p class="mb-0" v-for="result in results" :key="'interp-' + result.resultID">
                        {{ result.interpretation }}
                      </p>
                    </div>

                    <!-- Comments -->
                    <div v-if="hasComments" class="mb-4 p-3 bg-light rounded">
                      <h6 class="text-muted mb-2">COMMENTS</h6>
                      <p class="mb-0" v-for="result in results" :key="'comment-' + result.resultID">
                        {{ result.comments }}
                      </p>
                    </div>

                    <!-- Signatures -->
                    <div class="row mt-5 pt-4 border-top">
                      <div class="col-md-6">
                        <div class="signature-block">
                          <p class="mb-1 fw-medium">{{ getLabTechName }}</p>
                          <p class="text-muted small mb-0">Laboratory Technician</p>
                          <p class="text-muted small mb-0">{{ formatDate(getResultDate) }}</p>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="signature-block">
                          <p class="mb-1 fw-medium" :class="{'text-muted': !isVerified}">
                            {{ getVerifiedByName || '________________' }}
                          </p>
                          <p class="text-muted small mb-0">Verified By</p>
                          <p class="text-muted small mb-0">
                            <span v-if="isVerified">{{ formatDate(getVerifiedDate) }}</span>
                            <span v-else class="text-warning"><i class="mdi mdi-clock-outline me-1"></i>Pending Verification</span>
                          </p>
                        </div>
                      </div>
                    </div>

                    <!-- Verification Status -->
                    <div class="text-center mt-4">
                      <span v-if="isVerified" class="badge bg-success p-2">
                        <i class="mdi mdi-check-decagram me-1"></i> VERIFIED
                      </span>
                      <span v-else class="badge bg-warning text-dark p-2">
                        <i class="mdi mdi-clock-outline me-1"></i> PENDING VERIFICATION
                      </span>
                    </div>

                    <!-- Footer -->
                    <div class="report-footer text-center mt-4 pt-3 border-top">
                      <small class="text-muted">
                        Report generated on {{ formatDate(new Date()) }} | {{ facilityName }} Laboratory Services
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">County Hospital Management System</div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>

    <!-- Toast Notification -->
    <ToastNotification ref="toast" />
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ToastNotification from "@/components/hospital/ToastNotification.vue";
import { execute, hospital } from "@/api";

export default {
  name: "LabReport",
  components: { NavigationBar, ToastNotification },
  data() {
    return {
      loading: true,
      verifying: false,
      requestID: '',
      request: {},
      results: [],
      
      // Session
      facilityID: '',
      facilityName: '',
      sessionNames: '',
      sessionIdNo: '',
      userID: ''
    };
  },
  computed: {
    hasInterpretation() {
      return this.results.some(r => r.interpretation);
    },
    hasComments() {
      return this.results.some(r => r.comments);
    },
    isVerified() {
      return this.results.length > 0 && this.results.every(r => r.status === 'Verified');
    },
    canVerify() {
      return this.results.length > 0 && this.results.some(r => r.status !== 'Verified');
    },
    getLabTechName() {
      return this.results.length > 0 ? this.results[0].labTechName : '-';
    },
    getResultDate() {
      return this.results.length > 0 ? this.results[0].resultDate : null;
    },
    getVerifiedByName() {
      const verified = this.results.find(r => r.verifiedByName);
      return verified ? verified.verifiedByName : null;
    },
    getVerifiedDate() {
      const verified = this.results.find(r => r.verifiedAt);
      return verified ? verified.verifiedAt : null;
    }
  },
  methods: {
    goTo(route) {
      this.$router.push('/' + route);
    },

    showSuccess(message) {
      if (this.$refs.toast) {
        this.$refs.toast.success(message);
      }
    },

    showError(message) {
      if (this.$refs.toast) {
        this.$refs.toast.error(message);
      } else {
        alert(message);
      }
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleString('en-GB', { 
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' 
      });
    },

    getFlagClass(flag) {
      if (!flag) return '';
      if (flag.includes('Critical')) return 'text-danger';
      if (flag === 'High' || flag === 'Low') return 'text-warning';
      return '';
    },

    getFlagBadge(flag) {
      if (!flag) return 'bg-success';
      if (flag.includes('Critical')) return 'bg-danger';
      if (flag === 'High' || flag === 'Low') return 'bg-warning text-dark';
      return 'bg-info';
    },

    loadReport() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getLabRequest");
      data.append("requestID", this.requestID);

      execute(data, hospital)
        .then((res) => {
          this.loading = false;
          if (res.data.success && res.data.data.request) {
            this.request = res.data.data.request;
            this.results = res.data.data.results || [];
          } else {
            this.showError('Lab request not found');
            this.goTo('lab-dashboard');
          }
        })
        .catch((e) => {
          this.loading = false;
          console.error(e);
          this.showError('Error loading report');
        });
    },

    verifyResult() {
      if (this.results.length === 0) return;

      this.verifying = true;

      // Verify all unverified results
      const promises = this.results
        .filter(r => r.status !== 'Verified')
        .map(result => {
          const data = new FormData();
          data.append("function", "verifyLabResult");
          data.append("resultID", result.resultID);
          data.append("verifiedByID", this.userID);
          data.append("verifiedByName", this.sessionNames);
          data.append("sessionNames", this.sessionNames);
          data.append("sessionIdNo", this.sessionIdNo);
          return execute(data, hospital);
        });

      Promise.all(promises)
        .then(() => {
          this.verifying = false;
          this.showSuccess('Result verified successfully');
          this.loadReport();
        })
        .catch((e) => {
          this.verifying = false;
          console.error(e);
          this.showError('Error verifying result');
        });
    },

    printReport() {
      const printContent = document.getElementById('labReport').innerHTML;
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
        <head>
          <title>Lab Report - ${this.request.requestID}</title>
          <link href="assets/css/bootstrap.min.css" rel="stylesheet">
          <style>
            body { padding: 20px; font-family: Arial, sans-serif; }
            .badge { padding: 5px 10px; border-radius: 4px; font-size: 12px; }
            .bg-success { background-color: #34c38f !important; color: white; }
            .bg-warning { background-color: #f1b44c !important; }
            .bg-danger { background-color: #f46a6a !important; color: white; }
            .bg-info { background-color: #50a5f1 !important; color: white; }
            .text-danger { color: #f46a6a !important; }
            .text-warning { color: #f1b44c !important; }
            .text-success { color: #34c38f !important; }
            .text-primary { color: #556ee6 !important; }
            .text-muted { color: #74788d !important; }
            .fw-bold { font-weight: bold; }
            .fw-medium { font-weight: 500; }
            .border-bottom { border-bottom: 1px solid #dee2e6; }
            .border-top { border-top: 1px solid #dee2e6; }
            .table { width: 100%; border-collapse: collapse; }
            .table th, .table td { padding: 8px; text-align: left; }
            .table-bordered th, .table-bordered td { border: 1px solid #dee2e6; }
            .table-light { background-color: #f8f9fa; }
            .signature-block { text-align: center; }
            @media print {
              .no-print { display: none !important; }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
        </html>
      `);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  },
  mounted() {
    this.requestID = this.$route.params.id;
    this.facilityID = sessionStorage.getItem("hospitalFacilityID") || '';
    this.facilityName = sessionStorage.getItem("hospitalFacilityName") || 'County Hospital';
    this.sessionNames = sessionStorage.getItem("sessionNames") || '';
    this.sessionIdNo = sessionStorage.getItem("sessionIdNo") || '';
    this.userID = sessionStorage.getItem("staffID") || sessionStorage.getItem("userID") || '';

    if (this.requestID) {
      this.loadReport();
    } else {
      this.showError('No request specified');
      this.goTo('lab-dashboard');
    }
  }
};
</script>

<style scoped>
.card {
  border: none;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}

.report-header h3 {
  color: #333;
  font-weight: 600;
}

.signature-block {
  text-align: center;
  padding-top: 40px;
  border-top: 1px solid #333;
  margin-top: 20px;
}

@media print {
  .no-print {
    display: none !important;
  }
  
  .card {
    box-shadow: none;
    border: 1px solid #ddd;
  }
}
</style>
